import { useEffect, useState } from 'react';

import { Card, Skeleton, Table } from 'antd';
import dayjs from 'dayjs';
import numeral from 'numeral';

import { get } from '@/libs/axios';

interface TableRecord {
  pharmacy_name: string;
  eob_created_at: string;
  created_at: string;
  status: number;
  amount_paid: number;
  total_amount_paid: number;
  estimated_patient_out_of_pocket: number;
  expected_dispensing_date: string;
  date_of_service: string;
  amount_requested: string;
  dispense_date: string;
  fill_number: number;
  requested: number;
  authorized: number;
  hicf_updated_at: string;
  eob_updated_at: string;
  paid_at: string;
  paid: number;
  prescriber: string;
}

const claimColumns = [
  {
    title: 'Request Date',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (_: unknown, record: TableRecord) => dayjs(record.created_at).format('MM-DD-YYYY'),
  },
  {
    title: 'Dispense Date',
    dataIndex: 'date_of_service',
    key: 'date_of_service',
    render: (_: unknown, record: TableRecord) => dayjs(record.date_of_service).format('MM-DD-YYYY'),
  },
  {
    title: 'Fill #',
    dataIndex: 'fill_number',
    key: 'fill_number',
  },
  {
    title: 'Pharmacy Name',
    dataIndex: 'pharmacy_name',
    key: 'pharmacy_name',
    render: (_: unknown, record: TableRecord) => {
      if (record.pharmacy_name) {
        return record.pharmacy_name;
      } else {
        return 'Not Available';
      }
    },
  },
  {
    title: 'Assistance Requested',
    dataIndex: 'amount_requested',
    key: 'amount_requested',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.amount_requested).format('$0,0.00');
    },
  },
  {
    title: 'Assistance Provided',
    dataIndex: 'total_amount_paid',
    key: 'total_amount_paid',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.total_amount_paid).format('$0,0.00');
    },
  },
];

const columns = [
  {
    title: 'Request Date',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (_: unknown, record: TableRecord) => dayjs(record.created_at).format('MM-DD-YYYY'),
  },
  {
    title: 'Dispense Date',
    dataIndex: 'expected_dispensing_date',
    key: 'expected_dispensing_date',
    render: (_: unknown, record: TableRecord) =>
      dayjs(record.expected_dispensing_date).format('MM-DD-YYYY'),
  },
  {
    title: 'Fill #',
    dataIndex: 'fill_num',
    key: 'fill_num',
  },
  {
    title: 'Pharmacy Name',
    dataIndex: 'pharmacy_name',
    key: 'pharmacy_name',
    render: (_: unknown, record: TableRecord) => {
      if (record.pharmacy_name) {
        return record.pharmacy_name;
      } else {
        return 'Not Available';
      }
    },
  },
  {
    title: 'Requested',
    dataIndex: 'estimated_patient_out_of_pocket',
    key: 'estimated_patient_out_of_pocket',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.estimated_patient_out_of_pocket).format('$0,0.00');
    },
  },
  {
    title: 'Authorized',
    dataIndex: 'total_amount_paid',
    key: 'total_amount_paid',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.total_amount_paid).format('$0,0.00');
    },
  },
  {
    title: 'EOB Date',
    dataIndex: 'eob_created_at',
    key: 'eob_created_at',
    render: (_: unknown, record: TableRecord) => {
      if (record.eob_created_at) {
        return dayjs(record.eob_created_at).format('MM-DD-YYYY');
      }
      return 'Not Submitted';
    },
  },
  {
    title: 'Paid',
    dataIndex: 'status',
    key: 'status',
    render: (_: unknown, record: TableRecord) => {
      if (record.status === 0) {
        return 'EOB Pending';
      } else if (record.status === 1) {
        return 'EOB in Review';
      } else if (record.status === 2) {
        return numeral(record.amount_paid).format('$0,0.00');
      } else {
        return 'n/a';
      }
    },
  },
];

const pastMedicalColumns = [
  {
    title: 'Request',
    dataIndex: 'request',
  },
  {
    title: 'Request Date',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (_: unknown, record: TableRecord) => dayjs(record.created_at).format('MM-DD-YYYY'),
  },
  {
    title: ' Dispense Date',
    dataIndex: 'dispense_date',
    render: (_: unknown, record: TableRecord) => dayjs(record.dispense_date).format('MM-DD-YYYY'),
  },
  {
    title: 'Fill #',
    dataIndex: 'fill',
  },
  {
    title: 'Pharmacy Name',
    dataIndex: 'pharmacy_name',
  },
  {
    title: ' Requested',
    dataIndex: 'requested',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.requested).format('$0,0.00');
    },
  },
  {
    title: 'Authorized',
    dataIndex: 'authorized',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.authorized).format('$0,0.00');
    },
  },
  {
    title: 'EOB/HICF Date',
    dataIndex: 'paid_date',
    render: (_: unknown, record: TableRecord) => {
      const latestDate = [record.hicf_updated_at, record.eob_updated_at]
        .filter((date) => date)
        .sort((a, b) => dayjs(b).unix() - dayjs(a).unix())[0];

      if (!latestDate) {
        return 'Not Submitted';
      }

      return dayjs(latestDate).format('MM-DD-YYYY');
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
  },
  {
    title: 'Paid At',
    dataIndex: 'paid_at',
    render: (_: unknown, record: TableRecord) => {
      return record.paid_at ? dayjs(record.paid_at).format('MM-DD-YYYY') : 'N/A';
    },
  },
  {
    title: 'Paid',
    dataIndex: 'paid',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.paid).format('$0,0.00');
    },
  },
  {
    title: 'Prescriber',
    dataIndex: 'prescriber',
  },
  {
    title: 'Insurance',
    dataIndex: 'insurance',
  },
];

const pastPharmacyColumns = [
  {
    title: 'Claim ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Request Date',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (_: unknown, record: TableRecord) => dayjs(record.created_at).format('MM-DD-YYYY'),
  },
  {
    title: 'Dispense Date',
    dataIndex: 'expected_dispensing_date',
    key: 'expected_dispensing_date',
    render: (_: unknown, record: TableRecord) =>
      dayjs(record.expected_dispensing_date).format('MM-DD-YYYY'),
  },
  {
    title: 'Fill #',
    dataIndex: 'fill_number',
    key: 'fill_number',
  },
  {
    title: 'Pharmacy Name',
    dataIndex: 'pharmacy_name',
    key: 'pharmacy_name',
    render: (_: unknown, record: TableRecord) => {
      if (record.pharmacy_name) {
        return record.pharmacy_name;
      } else {
        return 'Not Available';
      }
    },
  },
  {
    title: 'Assistance Requested',
    dataIndex: 'amount_requested',
    key: 'amount_requested',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.amount_requested).format('$0,0.00');
    },
  },
  {
    title: 'Assistance Provided',
    dataIndex: 'total_amount_paid',
    key: 'total_amount_paid',
    render: (_: unknown, record: TableRecord) => {
      return numeral(record.total_amount_paid).format('$0,0.00');
    },
  },
  {
    title: 'Prescriber',
    dataIndex: 'prescriber',
    key: 'prescriber',
  },
  {
    title: 'Insurance',
    dataIndex: 'insurance',
    key: 'insurance',
  },
];

const ClaimStatus = () => {
  const [tableLoading, setTableLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [enrollment, setEnrollment] = useState<any>({});
  const [assistanceRequests, setAssistanceRequests] = useState<any>([]);
  const [pastEnrollments, setPastEnrollments] = useState<any>([]);

  const loadData = async () => {
    try {
      const { data: enrollment } = await get('program_enrollments/detail');
      const { data: assistanceRequests } = await get('assistance_requests');
      const { data: pastEnrollments } = await get('program_enrollments/past_enrollments');
      const { data: claims } = await get('claims');

      setEnrollment(enrollment);
      setClaims(claims);
      setAssistanceRequests(assistanceRequests);
      setPastEnrollments(pastEnrollments);
    } catch (e) {
      console.error(e);
    } finally {
      setTableLoading(false);
    }
  };

  const hasEnrollment = !!Object.keys(enrollment).length;

  useEffect(() => {
    loadData();
  }, []);

  if (tableLoading) {
    return <Skeleton className="mb-10" paragraph={{ rows: 10 }} active />;
  }

  return (
    <>
      {hasEnrollment && (
        <Card className="mb-10">
          <div className="text-2xl font-bold">
            Current enrollment begin date
            {enrollment?.date_of_enrollment && (
              <span className="text-xl text-zinc-500">
                &nbsp;({dayjs(enrollment?.date_of_enrollment).format('MM-DD-YYYY')})
              </span>
            )}
          </div>
          <div className="my-4 flex justify-between font-semibold">
            <span>
              ASSISTANCE RECEIVED: {numeral(enrollment.total_amount_paid).format('$0,0.00')}
            </span>
            <span>
              ASSISTANCE REMAINING: {numeral(enrollment.total_remaining).format('$0,0.00')}
            </span>
          </div>
          {claims.length > 0 && (
            <>
              <p className="bg-green-100 text-green-700 p-2 rounded mb-1">Pharmacy benefits</p>
              <Table
                loading={tableLoading}
                rowKey="id"
                bordered
                scroll={{ x: 'max-content' }}
                dataSource={claims}
                columns={claimColumns}
                pagination={false}
              />
            </>
          )}
          {assistanceRequests.length > 0 && (
            <>
              <p className="bg-green-100 text-green-700 p-2 rounded mb-1 mt-10">Medical benefits</p>
              <Table
                loading={tableLoading}
                rowKey="id"
                bordered
                dataSource={assistanceRequests}
                columns={columns}
                scroll={{ x: 'max-content' }}
                pagination={false}
              />
            </>
          )}
        </Card>
      )}
      {pastEnrollments.length > 0 && (
        <Card className="mt-10" title="Past Enrollments">
          <div className="gap-y-6 flex flex-col">
            {pastEnrollments.map((enrollment: any) => (
              <div className="" key={enrollment.id}>
                <p className="uppercase mt-3 mb-1 font-semibold">
                  Enrollment date: {dayjs(enrollment.date_of_enrollment).format('MM-DD-YYYY')}{' '}
                  (Authorized: {numeral(enrollment.total_authorized).format('$0,0.00')} | Paid:{' '}
                  {numeral(enrollment.total_paid_out).format('$0,0.00')})
                </p>
                {enrollment.claims.length > 0 && (
                  <div>
                    <p className="bg-green-100 text-green-700 p-2 rounded mb-1">
                      Pharmacy benefits
                    </p>
                    <Table
                      loading={tableLoading}
                      rowKey="id"
                      bordered
                      scroll={{ x: 'max-content' }}
                      pagination={false}
                      dataSource={enrollment.claims}
                      columns={pastPharmacyColumns}
                    />
                  </div>
                )}
                {enrollment.assistance_request?.length > 0 && (
                  <div>
                    <p className="bg-green-100 text-green-700 p-2 rounded mb-1">Medical benefits</p>
                    <Table
                      loading={tableLoading}
                      rowKey="id"
                      bordered
                      scroll={{ x: 'max-content' }}
                      pagination={false}
                      dataSource={enrollment.assistance_request}
                      columns={pastMedicalColumns}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
};

export default ClaimStatus;
